import clsx from 'clsx';
import { useParams, useSearchParams } from 'react-router';
import { fmtMonthDay } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { ApiError, errorText } from '../../lib/api';
import { useLayout } from '../../lib/media';
import { useProgram } from '../../lib/queries';
import { Avatar, EmptyState, Segmented, Skeleton } from '../../ui/primitives';
import { CharterTab } from './ProgramCharter';
import { ReportTab } from './ProgramReport';
import { TasksTab } from './ProgramTasks';
import './programs.css';

// 一个项目集：进度汇报 / 关键任务 / 项目说明（PID）三页。

type Tab = 'report' | 'tasks' | 'charter';
const TABS: { value: Tab; label: string }[] = [
  { value: 'report', label: '进度汇报' },
  { value: 'tasks', label: '关键任务' },
  { value: 'charter', label: '项目说明' },
];

export function ProgramPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const mobile = useLayout() === 'mobile';
  const { data, isLoading, error } = useProgram(id);
  const tab = (TABS.some((t) => t.value === params.get('tab')) ? params.get('tab') : 'report') as Tab;
  const setTab = (t: Tab) =>
    setParams(
      (p) => {
        p.set('tab', t);
        return p;
      },
      { replace: true },
    );

  if (error) {
    const forbidden = error instanceof ApiError && error.status === 403;
    return (
      <>
        {!mobile && <TopBar crumbs={['项目集']} icon="list" />}
        <EmptyState icon={forbidden ? 'lock' : 'wifioff'} title={forbidden ? '看不到这个项目集' : '项目集没有加载出来'} desc={errorText(error)} />
      </>
    );
  }
  if (isLoading || !data)
    return (
      <div style={{ padding: '40px 48px' }} aria-busy="true">
        <Skeleton w={240} h={22} />
        <Skeleton w={360} h={12} style={{ marginTop: 12 }} />
      </div>
    );

  return (
    <>
      {!mobile && (
        <TopBar crumbs={['项目集', data.name]} icon="list">
          <Segmented items={TABS} value={tab} onChange={setTab} />
        </TopBar>
      )}
      <div className={clsx('pg', mobile && 'mobile')}>
        <header className="pg-head">
          <div className="pg-code">{data.code}</div>
          <h1 className="t-page-title">{data.name}</h1>
          <div className="pg-meta">
            <span>
              项目经理 <Avatar initial={data.pm.initial} seed={data.pm.name} size={16} /> {data.pm.name}
            </span>
            {data.sponsor && <span>决策人 {data.sponsor.name}</span>}
            {(data.startDate || data.endDate) && (
              <span>
                {data.startDate ? fmtMonthDay(data.startDate) : '?'} – {data.endDate ? fmtMonthDay(data.endDate) : '?'}
              </span>
            )}
            <span>
              {data.taskCount} 个关键任务 · 完成 {data.doneCount}
              {data.delayedCount > 0 && <span className="c-red"> · 延后 {data.delayedCount}</span>}
              {data.blockedCount > 0 && <span className="c-red"> · 卡住 {data.blockedCount}</span>}
            </span>
            {data.status === 'closed' && <span className="c4">已关闭</span>}
          </div>
          {mobile && (
            <div style={{ marginTop: 12 }}>
              <Segmented items={TABS} value={tab} onChange={setTab} />
            </div>
          )}
        </header>
        {tab === 'report' && <ReportTab program={data} mobile={mobile} />}
        {tab === 'tasks' && <TasksTab program={data} mobile={mobile} />}
        {tab === 'charter' && <CharterTab program={data} />}
      </div>
    </>
  );
}
